import { Card, Text, Title } from "@clipboard-health/ui-components";
import { Box, CardActionArea, Skeleton, Stack } from "@mui/material";
import { DeprecatedGlobalAppV1Paths } from "@src/appV2/App/paths";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useHistory } from "react-router-dom";

import { ACCOUNT_ROLES_FULL_PATH } from "../../Accounts/Roles/paths";
import { IconButton } from "../../components/IconButton";
import { useIsJobInterviewsEnabled } from "../../Placements/useIsJobInterviewsEnabled";
import { useIsSurfaceShiftsEnabled } from "../../ShiftDiscovery/useIsSurfaceShiftsEnabled";
import {
  type OnboardingRequirement,
  OnboardingRequirementStatuses,
  useOnboardingRequirement,
} from "../hooks/useOnboardingRequirement";

function getBannerProps(
  requirement: OnboardingRequirement,
  isJobInterviewsEnabled: boolean
): {
  title: string;
  description?: string;
} {
  if (requirement.status === OnboardingRequirementStatuses.MISSING) {
    return {
      title: `Add an applicable ${requirement.getDisplayName()} to start ${
        isJobInterviewsEnabled ? "booking interviews" : "applying jobs"
      }`,
    };
  }

  if (requirement.status === OnboardingRequirementStatuses.REJECTED) {
    return {
      title: `There was a problem with your ${requirement.getDisplayName()}. Please re-submit.`,
    };
  }

  if (requirement.status === OnboardingRequirementStatuses.EXPIRED) {
    return {
      title: `Your ${requirement.getDisplayName()} ${
        requirement.subItems.length > 1 ? "are" : "is"
      } expired. Please re-submit.`,
    };
  }

  return {
    title: `Your ${requirement.getDisplayName()} is currently under review`,
  };
}

interface OnboardingRequirementsBannerProps {
  workerId: string;
}

export function OnboardingRequirementsBanner(props: OnboardingRequirementsBannerProps) {
  const { workerId } = props;
  const history = useHistory();
  const { requirement, isInvalid, isLoading, isCompleted } = useOnboardingRequirement(workerId);
  const isJobInterviewsEnabled = useIsJobInterviewsEnabled();
  const isSurfaceShiftsEnabled = useIsSurfaceShiftsEnabled();

  useLogEffect(
    APP_V2_APP_EVENTS.PLACEMENT_ONBOARDING_BANNER_VIEWED,
    {
      requirement,
    },
    {
      enabled: !isLoading,
    }
  );

  if (isLoading) {
    return <Skeleton animation="wave" variant="rounded" height={164} />;
  }

  if (isCompleted) {
    return null;
  }

  const bannerProps = getBannerProps(requirement, isJobInterviewsEnabled);

  return (
    <Card
      sx={{
        padding: 7,
        background: (theme) =>
          isInvalid
            ? theme.palette.onboarding?.error.gradient
            : theme.palette.onboarding?.inProgress.gradient,
      }}
    >
      <CardActionArea
        onClick={() => {
          logEvent(APP_V2_APP_EVENTS.PLACEMENT_ONBOARDING_BANNER_CTA_CLICKED, {
            requirement,
          });
          const href = isSurfaceShiftsEnabled
            ? ACCOUNT_ROLES_FULL_PATH
            : DeprecatedGlobalAppV1Paths.LICENSE_MANAGER;
          history.push(href);
        }}
      >
        <Stack spacing={9}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 7,
            }}
          >
            <Stack spacing={2}>
              <Title variant="h5" component="h2" sx={{ fontWeight: 500 }}>
                {bannerProps.title}
              </Title>
              {bannerProps.description && <Text variant="body2">{bannerProps.description}</Text>}
            </Stack>
            <IconButton size="xSmall" variant="outlined" iconType="chevron-right" />
          </Box>
        </Stack>
      </CardActionArea>
    </Card>
  );
}
